import { InjectionToken, Provider } from '@angular/core';

/** A section's box relative to the scroller's viewport, in px; `top` is negative once scrolled past. */
export interface ActiveSectionCandidate {
  id: string;
  top: number;
  bottom: number;
}

export interface ActiveSectionContext {
  /** Eligible sections in page order: on the page, with height, and with a visible, enabled nav item. */
  sections: ActiveSectionCandidate[];
  viewportHeight: number;
  scrollTop: number;
  /** The largest scrollTop the page allows. */
  scrollMax: number;
  /** What was active before this evaluation, for hysteresis. */
  previous: string | null;
}

/**
 * Decides which section the navigation highlights (and the URL hash names) for the current scroll
 * position. Returns null when no section applies — above the first one, for instance — and the
 * page's top anchor is used instead.
 */
export type ActiveSectionStrategy = (context: ActiveSectionContext) => string | null;

export interface ActiveSectionStrategyOptions {
  /**
   * A section that has only just crossed the line, by less than this many px, does not take the
   * highlight away from the one that has it. Stops the highlight flapping when a title sits right
   * on the line. Default 4.
   */
  deadband?: number;
}

export interface BandActiveSectionStrategyOptions extends ActiveSectionStrategyOptions {
  /** The band at the top of the viewport a section has to touch, as a fraction of its height. Default 0.25. */
  band?: number;
  /**
   * When the page is scrolled as far as it goes, the last section is active even if it is too short
   * to reach the band. Without it, a short final section can never be highlighted, and clicking it
   * in the navigation lands on a page that highlights its predecessor. Default true.
   */
  endOfPage?: boolean;
}

export interface SlidingLineActiveSectionStrategyOptions extends ActiveSectionStrategyOptions {
  /** Where the line sits at the top of the page, as a fraction of the viewport height. Default 0.15. */
  start?: number;
  /** Where the line sits at the bottom of the page. Default 0.85. */
  end?: number;
}

/**
 * The section furthest down the page that touches the top `band` of the viewport. Deep inside a
 * long section nothing else touches the band, so it stays active until the next section's top
 * arrives. When nothing touches the band — a gap taller than the band, or scrolled past the last
 * section — the last section scrolled past keeps the highlight rather than it going dark.
 */
export function bandActiveSectionStrategy(options: BandActiveSectionStrategyOptions = {}): ActiveSectionStrategy {
  const { band = 0.25, endOfPage = true, deadband = 4 } = options;
  return (context) => {
    const line = context.viewportHeight * band;
    return pick(context, line, endOfPage, deadband);
  };
}

/**
 * Like the band strategy, but the line slides from `start` to `end` of the viewport as the page is
 * scrolled from top to bottom. Near the end of the page the line sits low, so whatever is visible
 * at the bottom becomes active without an end-of-page special case; the cost is that the switch
 * point is not at a fixed place on screen.
 */
export function slidingLineActiveSectionStrategy(options: SlidingLineActiveSectionStrategyOptions = {}): ActiveSectionStrategy {
  const { start = 0.15, end = 0.85, deadband = 4 } = options;
  return (context) => {
    const progress = context.scrollMax > 0 ? Math.min(1, Math.max(0, context.scrollTop / context.scrollMax)) : 0;
    const line = context.viewportHeight * (start + (end - start) * progress);
    return pick(context, line, false, deadband);
  };
}

function pick(context: ActiveSectionContext, line: number, endOfPage: boolean, deadband: number): string | null {
  const { sections, previous } = context;
  if (sections.length === 0) {
    return null;
  }
  if (endOfPage && context.scrollMax > 0 && context.scrollMax - context.scrollTop <= 2) {
    return sections[sections.length - 1].id;
  }

  let best: ActiveSectionCandidate | null = null;
  for (const section of sections) {
    if (section.top < line && section.bottom > 0) {
      best = section; // page order: the last one touching the band wins
    }
  }
  if (!best) {
    // Nothing touches the band. Stay with the last section scrolled past, if any.
    for (const section of sections) {
      if (section.top < line) {
        best = section;
      }
    }
  }
  if (!best) {
    return null;
  }

  if (previous !== null && previous !== best.id && best.top > line - deadband) {
    const current = sections.find((s) => s.id === previous);
    if (current && current.top < line && current.bottom > 0) {
      return previous; // the newcomer has barely crossed the line; don't flap
    }
  }
  return best.id;
}

/** Which strategy the scroll navigation uses. Defaults to the sliding-line strategy; override with {@link provideActiveSectionStrategy}. */
export const ACTIVE_SECTION_STRATEGY = new InjectionToken<ActiveSectionStrategy>('ACTIVE_SECTION_STRATEGY', {
  providedIn: 'root',
  factory: () => slidingLineActiveSectionStrategy(),
});

export function provideActiveSectionStrategy(strategy: ActiveSectionStrategy): Provider {
  return { provide: ACTIVE_SECTION_STRATEGY, useValue: strategy };
}
